import { ChangeEventHandler, FocusEventHandler, ReactNode, Ref, useId } from 'react';

interface CommonInputProps<T> {
	id?: string;
	name?: string;
	required?: boolean;
	invalid?: boolean;
	invalidText?: string;
	helperText?: string;
	readOnly?: boolean;
	onChange?: ChangeEventHandler<T> | undefined;
	onBlur?: FocusEventHandler<T> | undefined;
	defaultValue?: string | number;
	description?: string;
	optional?: boolean;
}

export interface UseFormInputProps<T> extends CommonInputProps<T> {
	label: string;
	hideLabel?: boolean;
	characterCount?: number;
	alwaysShowCharacterCount?: boolean;
}

export interface InputProps<T> extends CommonInputProps<T> {
	'aria-label'?: string;
	'aria-describedby'?: string;
	'aria-invalid'?: boolean;
	'aria-required'?: boolean;
	ref: Ref<T>;
}

export interface LabelProps {
	id?: string;
	htmlFor: string;
	optional?: boolean;
	children: ReactNode;
}

export interface HelpTextProps {
	id: string;
	invalid: boolean;
	children: ReactNode;
}

export interface DescriptionProps {
	id: string;
	children: ReactNode;
}

interface CharacterCountProps {
	limit?: number;
	alwaysShow?: boolean;
}

export interface UseInputReturn<T> {
	props: InputProps<T>;
	labelProps: LabelProps;
	descriptionProps: DescriptionProps;
	helpTextProps: HelpTextProps;
	characterCountProps: CharacterCountProps;
}

export function useInput<T>(
	{
		id,
		name,
		label,
		hideLabel,
		required,
		invalid,
		invalidText,
		helperText,
		readOnly,
		onChange,
		onBlur,
		defaultValue,
		characterCount,
		alwaysShowCharacterCount,
		description,
		optional,
	}: UseFormInputProps<T>,
	ref: Ref<T>,
): UseInputReturn<T> {
	// Get unique ID for the input element
	let inputId = useId();

	if (id) {
		inputId = id;
	}

	// Get id for description and help text component
	const helpTextId = `${inputId}-helpText`;
	const descriptionId = `${inputId}-description`;

	return {
		props: {
			id: inputId,
			required,
			onChange,
			onBlur,
			name,
			invalid,
			defaultValue,
			readOnly,
			'aria-label': hideLabel ? label : undefined,
			'aria-describedby': [descriptionId, helpTextId].join(' '),
			'aria-invalid': invalid || undefined,
			'aria-required': required || undefined,
			ref: ref,
		},
		labelProps: {
			optional: required ? false : optional,
			htmlFor: inputId,
			children: hideLabel ? null : label,
		},
		descriptionProps: {
			id: descriptionId,
			children: description ?? undefined,
		},
		helpTextProps: {
			id: helpTextId,
			invalid: !!(invalid && invalidText),
			children: invalid && invalidText ? invalidText : helperText,
		},
		characterCountProps: {
			limit: characterCount,
			alwaysShow: alwaysShowCharacterCount,
		},
	};
}
